'use client';

import { isBefore } from 'date-fns';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useTradeContext } from '../../../components/ui/TradePageClient/TradePageClient';
import type { PostOptionFormParams } from '../../../features/lottery/api/v2/postOptionForm';
import OptionBackBar from '../../../features/lottery/components/OptionBackBar/OptionBackBar';
import OptionNamedBar from '../../../features/lottery/components/OptionNamedBar/OptionNamedBar';
import OptionSelectableBar from '../../../features/lottery/components/OptionSelectableBar/OptionSelectableBar';
import { OptionInputNamedSelectableStepType, OptionInputType } from '../../../features/lottery/constants';
import type {
  LotteryOptionGoodsForm,
  OptionInputNamedSelectableStepTypeType,
  OptionInputTypeType,
} from '../../../features/lottery/types';
import { isValidOptionNameValue } from '../../../features/lottery/utils/isValidOptionNameValue';
import { swal } from '../../../lib';
import { newDate } from '../../../utils/date';
import { pickErrorMessages } from '../../../utils/functions';

export type OptionBarProps = {
  optionFormData?: LotteryOptionGoodsForm;
  goodsId: string;
  cutoffId: string;
  isEditMode?: boolean;
  lotteryHistoryId?: number;
  orderId?: string;
  postOptionForm: (params: PostOptionFormParams) => Promise<void>;
  step?: OptionInputNamedSelectableStepTypeType;
  onStepChange?: (step: OptionInputNamedSelectableStepTypeType) => void;
};

function OptionBar({
  optionFormData,
  cutoffId,
  isEditMode = false,
  lotteryHistoryId,
  orderId,
  postOptionForm,
  step = OptionInputNamedSelectableStepType.quantity.value,
  onStepChange,
}: OptionBarProps) {
  const { selectedItems } = useTradeContext();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [filledInputCount, setFilledInputCount] = useState(0);
  const [hasInvalidInput, setHasInvalidInput] = useState(false);
  const prevStepRef = useRef<OptionInputNamedSelectableStepTypeType>(step);

  const totalCount = Array.from(selectedItems.values()).reduce((sum, current) => sum + current, 0);

  useEffect(() => {
    if (!optionFormData) return;

    // named_selectableでstepが'quantity'の時は入力欄を監視しない
    if (
      optionFormData.optionInfo.type === OptionInputType.named_selectable.value &&
      step === OptionInputNamedSelectableStepType.quantity.value
    ) {
      setFilledInputCount(0);
      setHasInvalidInput(false);
      return;
    }

    const inputs = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      `input[name^="optionInput-${optionFormData.id}"], textarea[name^="optionInput-${optionFormData.id}"]`,
    );

    // 入力欄の文字の判定は OptionCollectionList が持っているが、ボタン側からは見えないため、画面上の入力欄の値をここでも判定する。
    // 数量や step が変わると入力欄を取り直すので、数量を減らして消えた入力欄のエラーが残ることはない。
    // 締め切り後の disabled の入力欄は「【自動入力】」が付き、直すこともできないため判定しない。
    const handleInputChange = () => {
      const inputList = Array.from(inputs);
      setFilledInputCount(inputList.filter((input) => input.value.trim() !== '').length);
      setHasInvalidInput(inputList.some((input) => !input.disabled && !isValidOptionNameValue(input.value)));
    };

    handleInputChange();

    inputs.forEach((input) => {
      input.addEventListener('input', handleInputChange);
    });

    return () => {
      inputs.forEach((input) => {
        input.removeEventListener('input', handleInputChange);
      });
    };
  }, [optionFormData, totalCount, step]);

  // 選べるオプション＆宛名オプション限定でstep が quantity/name のどちらか一方から、もう一方へ切り替わったかを判定する関数。
  // 以下のパターンで遷移した時のみtrueを返します。
  // 1) quantity -> name
  // 2) name -> quantity
  const isStepChangedBetweenQuantityAndName = (
    previousStep: OptionInputNamedSelectableStepTypeType,
    currentStep: OptionInputNamedSelectableStepTypeType,
  ) => {
    if (previousStep === OptionInputNamedSelectableStepType.quantity.value) {
      return currentStep === OptionInputNamedSelectableStepType.name.value;
    }
    return currentStep === OptionInputNamedSelectableStepType.quantity.value;
  };

  // 選べる＆宛名オプションに入力の場合、stepが'quantity'⇔'name'に変わるタイミングでスクロール位置を一番上に戻す。
  // スクロール位置の制御は、step変更に伴う再レンダリングでDOM構造が切り替わった「後」に実行する必要がある。
  // イベントハンドラ内で即時にscrollToすると、まだ旧DOMのまま/レイアウト未確定のタイミングに当たり、
  // 期待どおりに先頭へ戻らないことがあるため、依存値(optionFormData, step)の更新後に走るuseEffectで扱う。
  // さらにrequestAnimationFrameでブラウザ描画直前まで待つことで、実DOM反映後の安定したタイミングで確実にスクロールする。
  useEffect(() => {
    if (!optionFormData) {
      prevStepRef.current = step;
      return;
    }
    const isQuantityAndNameStepToggled = isStepChangedBetweenQuantityAndName(prevStepRef.current, step);
    prevStepRef.current = step;
    if (!isQuantityAndNameStepToggled) return;
    if (optionFormData.optionInfo.type !== OptionInputType.named_selectable.value) return;

    // step変更後に実DOMへ反映されてからスクロール位置を一番上に戻す
    const rafId = window.requestAnimationFrame(() => {
      const scrollTarget = document.scrollingElement;
      if (scrollTarget) {
        scrollTarget.scrollTo({ top: 0, behavior: 'smooth' });
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [optionFormData, step]);

  // 入力完了ボタン押下時の処理
  const handleSubmit = async () => {
    if (!optionFormData) return;

    if (
      optionFormData?.optionInfo.type === OptionInputType.named_selectable.value &&
      step === OptionInputNamedSelectableStepType.quantity.value
    ) {
      // 数量入力完了 → 次のステップへ
      onStepChange?.(OptionInputNamedSelectableStepType.name.value);
      return;
    }

    try {
      // 入力データを収集
      const optionData = collectOptionData();

      // API呼び出し
      const _ = await postOptionForm({
        lotteryPrizeGoodsId: optionFormData.id,
        lotteryCutoffId: Number(cutoffId),
        optionType: optionFormData.optionInfo.type as OptionInputTypeType,
        optionData: optionData as never, // 型安全性のため一時的にnever使用
        orderId: orderId,
      });

      // 成功時の処理（オプション一覧ページに戻る等）
      const queryParams = new URLSearchParams();

      // lotteryHistoryIdがある場合はそれを優先的にパラメータに含める
      if (lotteryHistoryId) {
        queryParams.set('lotteryHistoryId', lotteryHistoryId.toString());
      } else {
        // lotteryHistoryIdがない場合は従来通りのパラメータを含める
        // goodsId (lotteryPrizeGoodsId) を追加
        if (optionFormData.id) {
          queryParams.set('goodsId', optionFormData.id.toString());
        }

        if (cutoffId) {
          queryParams.set('cutoffId', cutoffId); // lottery_cutoff_id
        }
      }

      // orderIdは常に渡す
      if (orderId) {
        queryParams.set('orderId', orderId); // order_id
      }

      // 遷移元コンテキスト（from）を持ち回す
      const from = searchParams.get('from');
      if (from) {
        queryParams.set('from', from);
      }

      const url = `/account/collection/option${queryParams.toString() ? `?${queryParams.toString()}` : ''}` as const;
      router.push(url);
      router.refresh();
    } catch (error) {
      // 画面の判定をすり抜けて API で弾かれた場合も、ユーザーが原因に気づけるようにエラー内容を表示する
      await swal.messages({ messages: pickErrorMessages(error) });
    }
  };

  // 入力データを収集する関数
  const collectOptionData = () => {
    if (!optionFormData) return [];

    const optionType = optionFormData.optionInfo.type;

    if (optionType === 'named') {
      // named: テキスト入力から宛名を収集
      const inputs = document.querySelectorAll(
        `input[name^="optionInput-${optionFormData.id}-"], textarea[name^="optionInput-${optionFormData.id}-"]`,
      );
      const data: Array<{ hash: string | null; value: string }> = [];

      // 全てのinput/textareaから値を収集（空文字も含める）
      inputs.forEach((input) => {
        const value = (input as HTMLInputElement | HTMLTextAreaElement).value.trim();
        data.push({
          hash: null, // 新規入力
          value: value || '', // 空文字の場合も含める
        });
      });

      return data;
    }

    if (optionType === 'selectable') {
      // selectable: 選択された数量を収集
      const data: Array<{ hash: string; value: string }> = [];

      if (optionFormData.optionInfo.selectable) {
        optionFormData.optionInfo.selectable.forEach((selectableItem, index) => {
          const goodId = `${optionFormData.id}-${index}`;
          const count = selectedItems.get(goodId) || 0;
          if (count > 0) {
            data.push({
              hash: selectableItem.hash, // 正しい選択肢hash
              value: count.toString(),
            });
          }
        });
      }

      return data;
    }

    if (optionType === OptionInputType.named_selectable.value) {
      // named_selectable: 選択肢と宛名を収集
      const data: Array<{ hash: string; notes: Array<{ value: string }> }> = [];

      if (optionFormData.optionInfo.namedSelectable) {
        optionFormData.optionInfo.namedSelectable.forEach((namedSelectableItem, index) => {
          const goodId = `${optionFormData.id}-${index}`;
          const count = selectedItems.get(goodId) || 0;

          if (count > 0) {
            // この項目の入力フィールドを取得
            const itemInputs = document.querySelectorAll(
              `input[name^="optionInput-${optionFormData.id}-${index}-"], textarea[name^="optionInput-${optionFormData.id}-${index}-"]`,
            );
            const notes: Array<{ value: string }> = [];

            itemInputs.forEach((input) => {
              const value = (input as HTMLInputElement | HTMLTextAreaElement).value.trim();
              if (value) {
                notes.push({ value });
              }
            });

            data.push({
              hash: namedSelectableItem.hash,
              notes: notes,
            });
          }
        });
      }

      return data;
    }

    return [];
  };

  // 戻るボタンを押したときの処理
  const handleGoBack = () => {
    router.back();
  };

  // 宛名オプションUIで戻るボタンを押したときの処理
  const handleNamedGoBack = () => {
    if (
      optionFormData?.optionInfo.type === OptionInputType.named_selectable.value &&
      step === OptionInputNamedSelectableStepType.name.value
    ) {
      // 選べるオプション＆宛名オプションUIで、戻るボタンが押下された場合

      // 数量選択に戻る
      onStepChange?.(OptionInputNamedSelectableStepType.quantity.value);
      return;
    }

    // それ以外の場合は通常通りrouter.back()を呼び出す
    router.back();
  };

  // 期日が過ぎているかどうかの判定
  const isPastDeadline = () => {
    if (!optionFormData) return false;
    return isBefore(newDate(optionFormData.optionCutoffAt), newDate());
  };

  // 選べるオプションUIか否か
  const isSelectableBar = () => {
    if (!optionFormData) return false;
    if (optionFormData.optionInfo.type === 'selectable') return true;
    if (
      optionFormData.optionInfo.type === OptionInputType.named_selectable.value &&
      step === OptionInputNamedSelectableStepType.quantity.value
    )
      return true;
    return false;
  };

  // 確認ボタンの表示有無
  const showConfirmButton = () => {
    if (isEditMode && isPastDeadline()) return false;
    return true;
  };

  if (!showConfirmButton()) {
    // 入力完了ボタンが非表示の場合はOptionBackBarを利用する
    return <OptionBackBar onGoBack={handleGoBack} />;
  }

  if (isSelectableBar()) {
    // 選べるオプションUIの場合はOptionSelectableBarを利用する
    return (
      <OptionSelectableBar
        optionFormData={optionFormData}
        totalCount={totalCount}
        onGoBack={handleGoBack}
        onSubmit={handleSubmit}
      />
    );
  }

  // それ以外の場合はOptionNamedBarを利用する
  return (
    <OptionNamedBar
      optionFormData={optionFormData}
      filledInputCount={filledInputCount}
      hasInvalidInput={hasInvalidInput}
      onGoBack={handleNamedGoBack}
      onSubmit={handleSubmit}
    />
  );
}

export default OptionBar;
